// 案 C: 診療科ランキング型 (経営企画室向け 高密度)
// - 上部: 指標 select + 月 select
// - 病院全体行 (sticky)
// - 30 診療科を 表形式で 実績 / 予算比 / 前年比 で表示
// - 行タップで診療科 detail へ展開 (collapse 切替)

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../lib/api";
import { useMeta, TOP9_METRICS, ml } from "../lib/mobileApi";
import { MobileShell } from "../components/MobileShell";
import { fmtByUnit } from "../../lib/format";
import type { MonthlyResponse, Direction } from "@shared/api";

export function PrototypeC() {
  const { data: meta } = useMeta();
  const [metric, setMetric] = useState<string>("入院収入");
  const [asOfMonth, setAsOfMonth] = useState<string | null>(null);
  const [openDept, setOpenDept] = useState<string | null>(null);

  const dateMax = meta?.dateMax ?? "2026-04-01";
  const monthOptions = useMemo(() => {
    const opts: string[] = [];
    for (let i = 0; i < 12; i++) opts.push(monthShift(dateMax, -i));
    return opts;
  }, [dateMax]);

  if (!meta) return <div className="p-6 text-sm text-slate-400">読み込み中…</div>;

  const targetMonth = asOfMonth ?? meta.dateMax;
  const metricDef = meta.metrics.find((x) => x["集計区分"] === metric);
  const unit = metricDef?.unit ?? "";
  const direction = (metricDef?.direction ?? "neutral") as Direction;

  return (
    <MobileShell title="診療科ランキング" prototypeId="C" prototypeName="ランキング" asOfMonth={targetMonth}>
      <section className="space-y-1.5">
        <div className="flex gap-1.5">
          <select
            value={metric}
            onChange={(e) => setMetric(e.target.value)}
            className="flex-1 text-sm bg-white border border-slate-300 rounded px-2 py-1.5"
          >
            {TOP9_METRICS.map((m) => (
              <option key={m} value={m}>{ml(m)}</option>
            ))}
          </select>
          <select
            value={targetMonth}
            onChange={(e) => { setAsOfMonth(e.target.value); setOpenDept(null); }}
            className="text-sm bg-white border border-slate-300 rounded px-2 py-1.5"
          >
            {monthOptions.map((m) => (
              <option key={m} value={m}>{m.slice(0, 7)}</option>
            ))}
          </select>
        </div>
        <div className="text-[10px] text-slate-400 text-right">小分類 / 合計 / 合計 (固定)</div>
      </section>

      <RankingTable
        metric={metric}
        unit={unit}
        direction={direction}
        asOfMonth={targetMonth}
        openDept={openDept}
        onToggleDept={(d) => setOpenDept((prev) => (prev === d ? null : d))}
      />
    </MobileShell>
  );
}

function RankingTable({ metric, unit, asOfMonth, openDept, onToggleDept }: {
  metric: string; unit: string; direction: Direction; asOfMonth: string;
  openDept: string | null; onToggleDept: (d: string) => void;
}) {
  const { data, isLoading } = useQuery<MonthlyResponse>({
    queryKey: ["monthly-c", metric, asOfMonth],
    queryFn: () => api.monthly({
      iryo: "合計", yotei: "合計", metric,
      dateFrom: asOfMonth, dateTo: asOfMonth,
    }),
    placeholderData: (prev) => prev,
  });

  type Row = MonthlyResponse["rows"][number];
  const sorted = useMemo<{ all: Row[]; hospital: Row | null }>(() => {
    if (!data) return { all: [], hospital: null };
    const hospital = data.rows.find((r) => r["診療科"] === "総合計") ?? null;
    const all = data.rows
      .filter((r) => !["総合計", "急性期合計", "回復期合計"].includes(r["診療科"]))
      .sort((a, b) => (b.実績 ?? 0) - (a.実績 ?? 0));
    return { all, hospital };
  }, [data]);

  return (
    <section className="bg-white border border-slate-200 rounded-lg overflow-hidden">
      <div className="grid grid-cols-[1fr_auto_1.2rem] gap-2 px-3 py-2 bg-slate-100 text-[10px] font-semibold text-slate-600 border-b border-slate-200">
        <div>診療科</div>
        <div className="text-right">実績 (当月)</div>
        <div></div>
      </div>

      {sorted.hospital && (
        <RankRow
          rank={null}
          name="🏥 病院全体"
          val={sorted.hospital.実績}
          unit={unit}
          highlight
        />
      )}

      {sorted.all.map((r, i) => (
        <div key={r["診療科"]}>
          <button
            onClick={() => onToggleDept(r["診療科"])}
            className="w-full text-left"
          >
            <RankRow
              rank={i + 1}
              name={r["診療科"]}
              val={r.実績}
              unit={unit}
              active={openDept === r["診療科"]}
              chevron={openDept === r["診療科"] ? "▲" : "▼"}
            />
          </button>
          {openDept === r["診療科"] && (
            <DeptDetail dept={r["診療科"]} metric={metric} unit={unit} asOfMonth={asOfMonth} />
          )}
        </div>
      ))}
      {isLoading && (
        <div className="px-3 py-4 text-center text-[10px] text-slate-400">loading…</div>
      )}
    </section>
  );
}

function RankRow({ rank, name, val, unit, active, highlight, chevron }: {
  rank: number | null;
  name: string;
  val: number | null | undefined;
  unit: string;
  active?: boolean;
  highlight?: boolean;
  chevron?: string;
}) {
  return (
    <div className={`grid grid-cols-[1fr_auto_1.2rem] gap-2 px-3 py-2 text-xs border-b border-slate-100 items-center ${
      highlight ? "bg-slate-50 font-semibold" : active ? "bg-amber-50" : ""
    }`}>
      <div className="flex items-center gap-1.5 min-w-0">
        {rank != null && <span className="text-[9px] font-mono text-slate-400 w-4 shrink-0">{rank}</span>}
        <span className="text-slate-800 truncate">{name}</span>
      </div>
      <div className="text-right tabular-nums text-slate-700">{fmtByUnit(val, unit)}</div>
      <div className="text-right text-slate-300 text-[10px]">{chevron ?? ""}</div>
    </div>
  );
}

function DeptDetail({ dept, metric, unit, asOfMonth }: {
  dept: string; metric: string; unit: string; asOfMonth: string;
}) {
  const periodFrom = monthShift(asOfMonth, -11);
  const { data } = useQuery({
    queryKey: ["kpi-c-detail", metric, asOfMonth, dept],
    queryFn: () => api.kpi({
      granularity: "小", iryo: "合計", yotei: "合計", metric, asOfMonth, periodFrom, focusedDept: dept,
    }),
  });

  if (!data) return <div className="px-3 py-2 text-[10px] text-slate-400">読み込み中…</div>;
  const yoyDelta = data.current != null && data.prevYear != null && data.prevYear !== 0
    ? (data.current - data.prevYear) / Math.abs(data.prevYear) : null;
  const momDelta = data.current != null && data.prevMonth != null && data.prevMonth !== 0
    ? (data.current - data.prevMonth) / Math.abs(data.prevMonth) : null;

  return (
    <div className="bg-slate-50 px-3 py-2 border-b border-slate-200 text-[11px] space-y-1">
      <div className="grid grid-cols-3 gap-1.5">
        <div>
          <div className="text-slate-400">予算</div>
          <div className="font-semibold text-slate-700 tabular-nums">{fmtByUnit(data.budget, unit)}</div>
        </div>
        <div>
          <div className="text-slate-400">前年同月</div>
          <div className="font-semibold text-slate-700 tabular-nums">
            {fmtByUnit(data.prevYear, unit)}
            {yoyDelta != null && (
              <span className={`ml-1 text-[10px] ${yoyDelta >= 0 ? "text-blue-700" : "text-red-700"}`}>
                ({yoyDelta >= 0 ? "+" : ""}{(yoyDelta * 100).toFixed(1)}%)
              </span>
            )}
          </div>
        </div>
        <div>
          <div className="text-slate-400">前月</div>
          <div className="font-semibold text-slate-700 tabular-nums">
            {fmtByUnit(data.prevMonth, unit)}
            {momDelta != null && (
              <span className={`ml-1 text-[10px] ${momDelta >= 0 ? "text-blue-700" : "text-red-700"}`}>
                ({momDelta >= 0 ? "+" : ""}{(momDelta * 100).toFixed(1)}%)
              </span>
            )}
          </div>
        </div>
      </div>
      <div>
        <div className="text-slate-400">12ヶ月平均</div>
        <div className="font-semibold text-slate-700 tabular-nums">
          {fmtByUnit(data.periodAvg, unit)} <span className="text-slate-400 font-normal">(n={data.periodN})</span>
        </div>
      </div>
    </div>
  );
}

function monthShift(d: string, delta: number): string {
  const [y, m, day] = d.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1 + delta, day));
  return [dt.getUTCFullYear(), String(dt.getUTCMonth() + 1).padStart(2, "0"), String(dt.getUTCDate()).padStart(2, "0")].join("-");
}
